import beTwMerge from '@/utils/beTwMerge';
import Button from '@/components/homepage/Button';
import RichText from '@/components/RichText/RichText';
import Carousel from '@/components/Carousel/Carousel';
import type { ICarouselInfo } from '@/components/Carousel/Carousel';
import type { IItem, IItemWithGAEvents, IVideoItem } from '@/components/Carousel/types';
import { isRichTextEmpty } from '@/utils/builderUtils';
import useMediaQuery from '@/hooks/useMediaQuery';
import { subHeadersStyles } from '@/consts/builder';
import CarouselItem from './CarouselItem';

interface ISlickCarouselProps {
  eyeBrowText?: string;
  optionalHeader: string;
  headerTag?: 'h2' | 'h3';
  optionalSubHeader?: string;
  isCenterText?: boolean;
  isShowCarousel?: boolean;
  isMobileSlider?: boolean;
  isProduct?: boolean;
  isSetMaxWidth?: boolean;
  items: IItem[] | IVideoItem[] | IItemWithGAEvents[];
  carouselInfo: ICarouselInfo;
  cta?: {
    label: string;
    link: string;
  };
  className?: string;
  headerClassName?: string;
  slidesToShow?: number;
  subHeaderClassName?: string;
}

export default function SlickCarousel({
  eyeBrowText,
  optionalHeader,
  headerTag = 'h2',
  optionalSubHeader,
  carouselInfo = { name: 'carousel', type: 'category' },
  cta,
  isShowCarousel = true,
  isCenterText = false,
  isMobileSlider = true,
  isProduct = false,
  isSetMaxWidth = true,
  items,
  className,
  headerClassName = '',
  slidesToShow = 6,
  subHeaderClassName = 'tw-mb-8',
}: ISlickCarouselProps) {
  const { type } = carouselInfo;
  const isH2Tag = headerTag === 'h2';
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  const imagesClassNames = `${
    {
      product: 'tw-w-full tw-h-auto !tw-aspect-square tw-object-fill',
      category: 'tw-w-full tw-h-full tw-object-cover !tw-aspect-[384/445]',
      video: 'tw-w-full tw-h-auto !tw-aspect-square tw-object-fill',
      mission: 'tw-w-full tw-h-full tw-object-cover !tw-aspect-[384/445]',
    }[type]
  }`;
  const containerClassNames = `${
    {
      product: '',
      category: '',
      video: '!tw-aspect-square',
      mission: '',
    }[type]
  }`;

  const moreDescriptiveAriaLabel = (
    headerTitle: string | undefined,
    href: string | undefined,
    label: string
  ): string => {
    if (label && (label.toLowerCase().trim() === 'shop now' || label.toLowerCase().trim() === 'learn more')) {
      if (headerTitle) {
        return `${headerTitle} - ${label}`;
      }

      return `${label} on ${href?.replace(/\/$/, '').split('/').pop()?.replace(/-/g, ' ')} page`;
    }

    return label || '';
  };

  const renderButton = (btnClassName: string, ctaType: 'link' | 'secondary' = 'link') => {
    if (!cta || !cta.label) return null;
    return (
      <Button
        className={btnClassName}
        href={cta.link}
        type={ctaType}
        label={cta.label}
        ariaLabel={moreDescriptiveAriaLabel(optionalHeader, cta.link, cta.label)}
      />
    );
  };

  const renderHeaderBox = () => {
    switch (true) {
      case !optionalHeader:
        return null;
      case slidesToShow === 4:
        return (
          <div className="tw-flex tw-flex-col tw-justify-center tw-gap-0 large:tw-gap-8 large:tw-max-w-[459px] large:tw-pr-12">
            <div className={`${isCenterText ? 'tw-text-center' : ''}`}>
              {eyeBrowText && (
                <div className="tw-text-[13px] tw-font-book tw-tracking-[0.65px] tw-uppercase">{eyeBrowText}</div>
              )}
              <h2 className={`tw-whitespace-nowrap ${subHeadersStyles['h2']} tw-mb-4 tw-mt-0`}>{optionalHeader}</h2>
              {!isRichTextEmpty(optionalSubHeader) && (
                <RichText content={optionalSubHeader!} className={`${subHeaderClassName} tw-ml-0 large:tw-mb-0`} />
              )}
            </div>
            {isDesktop && cta && cta.label && (
              <div className={`tw-leading-[41px] tw-flex ${isCenterText ? 'tw-justify-center' : ''}`}>
                {renderButton('tw-font-avenirmedium tw-text-[14px]', 'secondary')}
              </div>
            )}
          </div>
        );
      case isH2Tag:
        return (
          <div
            className={beTwMerge(
              'tw-flex tw-justify-center md:tw-justify-between tw-items-center tw-ml-4 md:tw-ml-2 lg:tw-ml-0',
              !isRichTextEmpty(optionalSubHeader) ? 'tw-mb-2 desktop:tw-mb-4' : 'tw-mb-8',
              isProduct && 'tw-ml-0 tw-justify-between md:tw-justify-between desktop:tw-mb-2',
              isCenterText && 'tw-justify-center tw-text-center md:tw-justify-center',
              headerClassName
            )}
          >
            <h2
              className={beTwMerge(
                'md:tw-text-left tw-mb-0 ',
                type === 'mission' && 'tw-text-be-xl md:tw-text-be-2xl lg:tw-text-be-3xl tw-font-base tw tw-font-medium'
              )}
            >
              {optionalHeader}
            </h2>

            {!isCenterText &&
              renderButton(`tw-hidden md:tw-block tw-font-avenirmedium ${isProduct ? 'tw-text-[14px]' : ''}`)}
          </div>
        );
      default:
        return (
          <div className="tw-flex tw-w-full tw-items-center tw-justify-center tw-mb-8 tw-gap-4 large:tw-gap-8">
            {isDesktop && <div className="tw-h-[1px] tw-w-full tw-bg-[#ababab]"> </div>}
            <h3 className={`tw-my-0 tw-whitespace-nowrap ${subHeadersStyles['h3']}`}>{optionalHeader}</h3>
            {isDesktop && <div className="tw-h-[1px] tw-w-full tw-bg-[#ababab]"> </div>}
          </div>
        );
    }
  };

  const renderImageGallery = () => {
    if ((isDesktop && isShowCarousel) || (!isDesktop && isMobileSlider)) {
      return (
        <Carousel
          info={carouselInfo}
          isProduct={isProduct}
          items={items}
          customItemStyles={{
            arrowPositions: 'tw-top-[130px] md:tw-top-[166px] lg:tw-top-[calc(305px/2)] xl:tw-top-[calc(445px/2)]',
            container: containerClassNames,
            image: imagesClassNames,
          }}
          slidesToShow={isDesktop ? slidesToShow : 1.5}
        />
      );
    }
    return (
      <div className="tw-flex tw-w-full tw-items-start tw-gap-2 tw-flex-wrap large:tw-flex-nowrap">
        {items?.map((item, index) => (
          <div key={`${item.name}`} className="tw-flex tw-w-[calc(50%-4px)] large:tw-w-full">
            <CarouselItem
              index={index}
              isActive
              isProduct={isProduct}
              item={item}
              isGrid={items.length <= 4}
              customItemStyles={{
                arrowPositions: 'tw-top-[130px] md:tw-top-[166px] lg:tw-top-[calc(305px/2)] xl:tw-top-[calc(445px/2)]',
                container: '',
                image: imagesClassNames,
              }}
            />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      className={beTwMerge(
        `tw-ml-2 tw-mr-4 lg:tw-mx-8`,
        isSetMaxWidth && 'min-[1504px]:tw-mx-auto min-[1504px]:tw-max-w-[1440px]',
        isProduct && 'tw-mx-4',
        isMobileSlider && 'tw-mr-0',
        className
      )}
    >
      {slidesToShow !== 4 && (
        <>
          {renderHeaderBox()}
          {!isRichTextEmpty(optionalSubHeader) && isH2Tag && (
            <RichText content={optionalSubHeader!} className={subHeaderClassName} />
          )}
          {renderImageGallery()}
          {isProduct && (
            <div className="tw-flex tw-justify-center">
              {renderButton(
                `tw-mt-8 tw-font-avenirmedium tw-max-w-none md:tw-w-full large:tw-w-auto ${isMobileSlider ? 'tw-mr-4 large:tw-mr-0' : ''} ${!isCenterText && isH2Tag ? 'md:tw-hidden' : ''}`,
                'secondary'
              )}
            </div>
          )}
          {!isProduct && (
            <div className="md:tw-hidden tw-block tw-flex tw-justify-center tw-mt-6 md:tw-mt-0">
              {renderButton('', 'link')}
            </div>
          )}
        </>
      )}

      {slidesToShow === 4 && (
        <div className="tw-w-full tw-flex tw-py-10 tw-flex-col large:tw-flex-row">
          {renderHeaderBox()}
          {renderImageGallery()}
          {!isDesktop && (
            <div className={`tw-leading-[41px] ${isMobileSlider ? 'tw-mr-4' : ''}`}>
              {renderButton('tw-mt-8 tw-font-avenirmedium tw-max-w-none md:tw-w-full large:tw-w-auto', 'secondary')}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
